/**
 * Target list builders for the workspace rail.
 *
 * Separate from the component so the rail file exports only a component: Fast
 * Refresh cannot preserve state in a module that also exports helpers.
 */
import type { EquipmentTypeLabel } from '../hooks/useEquipmentTypes';
import type { DrawingValidation, PnidEquipment, PnidInstrument, PnidNode } from '../types';
import { getInstrumentDisplayName } from '../utils/instrumentTypes';

/** Three states, as the context bar's badge had them: an SME needs to tell a
 *  line that is signed off from one that has merely been touched. */
export type TargetStatus = 'reviewed' | 'marked' | 'unmarked';

export type TargetRailItem = {
  id: string;
  label: string;
  sub?: string;
  status: TargetStatus;
  /** The drawing's validation holds a finding on this element. */
  needsFix: boolean;
  /** The Type and Subtype filter facets, keyed as the API's `AgentReviewQueueItem` keys them. */
  element_type: string | null;
  subtype: string | null;
};

type TargetFacets = Pick<TargetRailItem, 'element_type' | 'subtype'>;

const MARKED_STATUSES = ['in_progress', 'final', 'final_and_reviewed'];

function targetStatus(status: string): TargetStatus {
  if (status === 'final_and_reviewed') return 'reviewed';
  return MARKED_STATUSES.includes(status) ? 'marked' : 'unmarked';
}

export function equipmentFacets(equipment: PnidEquipment): TargetFacets {
  return { element_type: equipment.equipment_type || null, subtype: equipment.subtype || null };
}

/** A line has a type and no subtype. */
export function nodeFacets(node: PnidNode): TargetFacets {
  return { element_type: node.line_type || null, subtype: null };
}

/** The trimmed top-level type, else the trimmed `core_fields` mirror; '' when neither has one. */
function instrumentType(instrument: PnidInstrument): string {
  const mirroredType = instrument.core_fields?.instrument_type;
  return instrument.instrument_type?.trim()
    || (typeof mirroredType === 'string' ? mirroredType.trim() : '');
}

export function instrumentFacets(instrument: PnidInstrument): TargetFacets {
  return { element_type: instrumentType(instrument) || null, subtype: instrument.subtype || null };
}

/** A line's row label: its number, then its line number when it has one. */
export function nodeTargetLabel(node: Pick<PnidNode, 'node_no' | 'line_no'>): string {
  return `#${node.node_no}${node.line_no ? ` ${node.line_no}` : ''}`;
}

/** An equipment's row label: its tag. */
export function equipmentTargetLabel(equipment: Pick<PnidEquipment, 'equipment_no'>): string {
  return equipment.equipment_no || 'Untitled';
}

/** Lines: unmarked (AI-identified) first, the queue of marking work, then by line number. */
export function nodeTargets(nodes: PnidNode[], validation: DrawingValidation): TargetRailItem[] {
  return [...nodes]
    .sort((a, b) => {
      const aMarked = a.status !== 'ai_identified';
      const bMarked = b.status !== 'ai_identified';
      if (aMarked !== bMarked) return aMarked ? 1 : -1;
      return a.node_no - b.node_no;
    })
    .map((node) => ({
      id: node.id,
      label: nodeTargetLabel(node),
      sub:
        node.source_ref && node.dest_ref
          ? `${node.source_ref} → ${node.dest_ref}`
          : undefined,
      status: targetStatus(node.status),
      needsFix: node.id in validation.elements,
      ...nodeFacets(node),
    }));
}

export type TargetRailGroup = {
  id: string;
  label: string;
  items: TargetRailItem[];
};

/** The heading of the group that holds the equipment with no type. */
const NO_EQUIPMENT_TYPE_LABEL = 'No type';

/** Every equipment on the drawing, one group per type headed by the type's label, the groups in
 *  label order and the untyped ones last under "No type". Inside a group, AI-identified first
 *  (the queue of work), then by tag. */
export function equipmentTargetGroups(
  equipments: PnidEquipment[],
  equipmentTypeLabel: EquipmentTypeLabel,
  validation: DrawingValidation,
): TargetRailGroup[] {
  const groups = new Map<string, TargetRailGroup>();
  const sorted = [...equipments].sort((a, b) => {
    if (a.status === 'ai_identified' && b.status !== 'ai_identified') return -1;
    if (a.status !== 'ai_identified' && b.status === 'ai_identified') return 1;
    return (a.equipment_no || '').localeCompare(b.equipment_no || '');
  });
  for (const equipment of sorted) {
    const facets = equipmentFacets(equipment);
    const type = facets.element_type ?? '';
    if (!groups.has(type)) {
      groups.set(type, { id: type, label: type ? equipmentTypeLabel(type) : NO_EQUIPMENT_TYPE_LABEL, items: [] });
    }
    groups.get(type)!.items.push({
      id: equipment.id,
      label: equipmentTargetLabel(equipment),
      status: targetStatus(equipment.status),
      needsFix: equipment.id in validation.elements,
      ...facets,
    });
  }
  return [...groups.values()].sort((left, right) => {
    if (!left.id !== !right.id) return left.id ? -1 : 1;
    return left.label.localeCompare(right.label);
  });
}

/** An instrument's row label: its trimmed tag, else the `core_fields` mirror, else its generated
 *  identity, else its type; '' when it has none of them. */
export function instrumentTargetLabel(instrument: PnidInstrument): string {
  const mirroredText = instrument.core_fields?.instrument_text;
  return instrument.instrument_text?.trim()
    || (typeof mirroredText === 'string' ? mirroredText.trim() : '')
    || instrument.generated_id || instrumentType(instrument);
}

/** Every instrument on the drawing, one group per type in first-seen order headed by the type's
 *  display name, and one row per annotation. An untagged instrument has no type, so its group
 *  has no heading. */
export function instrumentTargetGroups(instruments: PnidInstrument[], validation: DrawingValidation): TargetRailGroup[] {
  const groups = new Map<string, TargetRailGroup>();
  for (const instrument of instruments) {
    const type = instrumentType(instrument);
    const label = instrumentTargetLabel(instrument);
    if (!groups.has(type)) groups.set(type, { id: type, label: getInstrumentDisplayName(type), items: [] });
    groups.get(type)!.items.push({
      id: instrument.id, label, status: targetStatus(instrument.status),
      needsFix: instrument.id in validation.elements,
      ...instrumentFacets(instrument),
    });
  }
  return [...groups.values()].map((group) => ({
    ...group,
    items: group.items.sort((left, right) =>
      left.label.localeCompare(right.label) || left.id.localeCompare(right.id)),
  }));
}
